/**
 * frontend/src/pages/valuation/ValuationCompany.tsx
 *
 * VAL-COMPANY-PROFILE-1B — the "Company" step, between Engagement and Intake.
 * Facts about the business for the report's "The company" page. Record-only:
 * nothing here is scored and nothing here changes the number.
 *
 * Data provenance: nothing typed here lives only in the browser. The step
 * footer's Back / Save and continue (VAL-PATH-FOOTER) save first: an unchanged
 * profile sends nothing, a changed one is written once, a failure stays here
 * with the message inline. A profile over 100% still goes to the server — its
 * 422 message is what the user sees.
 *
 * VAL-PATH-COMPANY (P3): step 1 also shows what was set when the engagement
 * was started (read-only, _CompanyStartedBlock) and the engagement particulars
 * I1-I9 moved here from Intake. Changed particulars ride in the SAME PATCH as
 * the profile; unchanged ones are not sent. The profile fields live in
 * _CompanyProfileSection.tsx.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Skeleton } from '@/components/ui/skeleton'
import { getValuationEngagement } from '@/api/valuation'
import { saveCompanyProfile } from '@/api/valuationCompany'
import { fetchIndustryFactFields } from '@/api/valuationIndustryFacts'
import { fetchFinancials } from '@/api/valuationFinancials'
import { formatApiError } from '@/utils/httpErrors'
import type { EngagementWithCompany, IndustryFactField } from '@/types/valuationCompany'
import {
  BLANK_COMPANY,
  companyFromEngagement,
  companyToPayload,
  isDirty,
  missingPctMessages,
  incompletePersonMessages,
  failingRowCount,
  saveBlockedMessage,
  joinName,
  type CompanyState,
} from './_companyProfile'
import { CompanyProfileSection } from './_CompanyProfileSection'
import { CompanyStartedBlock } from './_CompanyStartedBlock'
import {
  BLANK_PARTICULARS,
  IntakeParticularsSection,
  particularsFromEngagement,
  particularsToPayload,
  type ParticularsState,
} from './_IntakeParticulars'
import { IndustryFacts } from './_IndustryFactsSection'
import { NO_OFFER, applyOffer, nameKey, offerFromReadings, type AgreementOffer, type Offered } from './_operatingAgreement'
import { factsDirty, factsFromSaved, factsToPayload, type FactsState } from './_industryFacts.ts'
import { ValuationStepper } from './_shared'
import { StepFooter } from '@/components/valuation/StepFooter'
import { StepBlocked, type StepSaveResult } from './_stepContext'

const particularsDirty = (a: ParticularsState, b: ParticularsState) => JSON.stringify(a) !== JSON.stringify(b)

export default function ValuationCompany() {
  const { valuationId } = useParams<{ valuationId: string }>()

  const [engagement, setEngagement] = useState<EngagementWithCompany | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [form, setForm] = useState<CompanyState>(BLANK_COMPANY)
  const [saved, setSaved] = useState<CompanyState>(BLANK_COMPANY)
  const [particulars, setParticulars] = useState<ParticularsState>(BLANK_PARTICULARS)
  const [savedParticulars, setSavedParticulars] = useState<ParticularsState>(BLANK_PARTICULARS)
  const [tried, setTried] = useState(false)
  const [factFields, setFactFields] = useState<IndustryFactField[]>([])
  const [setLabel, setSetLabel] = useState<string | null>(null)
  const [setLabels, setSetLabels] = useState<Record<string, string>>({})
  const [facts, setFacts] = useState<FactsState>({})
  const [savedFacts, setSavedFacts] = useState<FactsState>({})
  const [factsError, setFactsError] = useState<string | null>(null)
  const [offer, setOffer] = useState<AgreementOffer>(NO_OFFER)
  // Where each name that Use these filled came from; on screen only.
  const [ownerSources, setOwnerSources] = useState<Record<string, Offered[]>>({})
  const [personSources, setPersonSources] = useState<Record<string, Offered[]>>({})

  // VAL-OA-SCREEN-1: what the operating agreement reading found. A failed
  // fetch leaves the step exactly as it was.
  useEffect(() => {
    if (!valuationId) return
    let live = true
    fetchFinancials(valuationId)
      .then(view => { if (live) setOffer(offerFromReadings(view.operating_agreements)) })
      .catch(() => undefined)
    return () => { live = false }
  }, [valuationId])

  useEffect(() => {
    if (!valuationId) return
    let live = true
    getValuationEngagement(valuationId)
      .then(en => {
        if (!live) return
        const state = companyFromEngagement(en as EngagementWithCompany)
        setEngagement(en as EngagementWithCompany)
        setForm(state)
        setSaved(state)
        const loadedParticulars = particularsFromEngagement(en)
        setParticulars(loadedParticulars)
        setSavedParticulars(loadedParticulars)
        // K14: no industry and nothing kept -> there is nothing to ask the server.
        const industry = (en.industry ?? '').trim()
        if (!industry && !(en as EngagementWithCompany).industry_facts_kept) return
        fetchIndustryFactFields(industry)
          .then(res => {
            if (!live) return
            const loadedFacts = factsFromSaved(res.fields, (en as EngagementWithCompany).industry_facts)
            setFactFields(res.fields)
            setSetLabel(res.set_label)
            setSetLabels(res.set_labels)
            setFacts(loadedFacts)
            setSavedFacts(loadedFacts)
          })
          .catch((err: unknown) => { if (live) setFactsError(formatApiError(err)) })
      })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
    return () => { live = false }
  }, [valuationId])

  const set = <K extends keyof CompanyState>(key: K, value: CompanyState[K]) =>
    setForm(f => ({ ...f, [key]: value }))

  function useAgreement() {
    const had = (rows: Array<{ name: string }>) => new Set(rows.map(r => nameKey(r.name)))
    const hadOwners = had(form.owners)
    const hadPeople = had(form.key_people.map(k => ({ name: joinName(k.first, k.last) })))
    const group = (items: Offered[], skip: Set<string>) => {
      const out: Record<string, Offered[]> = {}
      for (const o of items) {
        const k = nameKey(o.name)
        if (!skip.has(k)) (out[k] ??= []).push(o)
      }
      return out
    }
    setOwnerSources(prev => ({ ...prev, ...group(offer.owners, hadOwners) }))
    setPersonSources(prev => ({ ...prev, ...group(offer.people, hadPeople) }))
    setForm(f => applyOffer(f, offer))
  }

  const notesFor = (sources: Record<string, Offered[]>) => (row: { name: string }): string[] => {
    const found = sources[nameKey(row.name)] ?? []
    return found.map(o => o.source)
      .filter((t, i, all) => all.indexOf(t) === i)
  }

  // A named row with no % is never sent as 0 — Save is blocked and the row named.
  const mixErrors = missingPctMessages('revenue', form.revenue_mix.map(r => ({ name: r.line, pct: r.pct })))
  const ownerErrors = missingPctMessages('ownership', form.owners)
  const personErrors = incompletePersonMessages(form.key_people)
  const failing = failingRowCount(mixErrors, ownerErrors, personErrors)

  /** Write what is on screen; throws when blocked or when the server refuses. */
  async function persist(): Promise<void> {
    setTried(true)
    // Blocked: the live red line below names the problem and clears as it is fixed.
    if (!valuationId || failing > 0) throw new StepBlocked(saveBlockedMessage(failing))
    const snapshot = form
    const factsSnapshot = facts
    const particularsSnapshot = particulars
    const factsChanged = factsDirty(factsSnapshot, savedFacts)
    const factsBody = factsChanged ? factsToPayload(factFields, factsSnapshot) : undefined
    if (particularsDirty(particularsSnapshot, savedParticulars)) {
      // One PATCH: profile (+ facts when changed) + particulars I1-I9.
      await saveCompanyProfile(valuationId, companyToPayload(snapshot), factsBody, particularsToPayload(particularsSnapshot))
      setSavedParticulars(particularsSnapshot)
    } else if (factsChanged) {
      await saveCompanyProfile(valuationId, companyToPayload(snapshot), factsBody)
    } else {
      await saveCompanyProfile(valuationId, companyToPayload(snapshot))
    }
    if (factsChanged) setSavedFacts(factsSnapshot)
    setSaved(snapshot)
  }

  /** The footer's save-first: nothing sent when nothing changed. */
  async function stepSave(): Promise<StepSaveResult> {
    if (!isDirty(form, saved) && !factsDirty(facts, savedFacts) && !particularsDirty(particulars, savedParticulars)) {
      return { changed: false, savedAt: null }
    }
    await persist()
    return { changed: true, savedAt: new Date().toISOString() }
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div className="rounded-lg border border-pal-error-border bg-pal-error-bg p-4 text-sm text-pal-error-text">{loadError}</div>
      </div>
    )
  }
  if (!engagement) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <h1 className="text-2xl font-bold text-pal-ink">Company profile</h1>
      <ValuationStepper current="company" engagementId={valuationId} />

      <CompanyStartedBlock engagement={engagement} />
      <IntakeParticularsSection particulars={particulars} setParticulars={setParticulars} />

      <form className="space-y-5" onSubmit={e => e.preventDefault()}>
        <CompanyProfileSection
          form={form}
          set={set}
          tried={tried}
          mixErrors={mixErrors}
          ownerErrors={ownerErrors}
          personErrors={personErrors}
          offer={offer}
          onUseAgreement={useAgreement}
          ownerNote={notesFor(ownerSources)}
          personNote={notesFor(personSources)}
        />

        <IndustryFacts
          fields={factFields}
          setLabel={setLabel}
          current={setLabel ?? (engagement.industry_label ?? engagement.industry ?? '').trim()}
          kept={engagement.industry_facts_kept ?? null}
          setLabels={setLabels}
          state={facts}
          onChange={(key, value) => setFacts(f => ({ ...f, [key]: value }))}
          loadError={factsError}
        />

      </form>
      {tried && failing > 0 && <p role="alert" className="mt-6 text-sm font-medium text-red-600">{saveBlockedMessage(failing)}</p>}
      <StepFooter step="company" engagementId={valuationId ?? ''} save={stepSave} />
    </div>
  )
}
